import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { IoChevronForward } from 'react-icons/io5';
import { getNotices, getNoticeCount } from '../../../services/noticeService';

const PER_PAGE = 5;

const NotificationList = () => {
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([getNotices(page, PER_PAGE), getNoticeCount()])
      .then(([list, count]) => {
        if (!alive) return;
        setItems(list);
        setTotal(count);
      })
      .catch(() => {
        if (!alive) return;
        setItems([]);
        setTotal(0);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const selected = items.find((n) => n.id === selectedId);

  if (selected) {
    return (
      <Container>
        <Meta>{selected.date} · {selected.createdBy || '관리자'} · {selected.target || '전체'}</Meta>
        <DetailTitle>{selected.title}</DetailTitle>
        <DetailBody>{selected.content}</DetailBody>
        <PrimaryBtn onClick={() => setSelectedId(null)}>목록보기</PrimaryBtn>
      </Container>
    );
  }

  return (
    <Container>
      <ListWrap>
        {loading && <Empty>불러오는 중...</Empty>}
        {!loading && items.length === 0 && <Empty>등록된 알림이 없습니다.</Empty>}
        {!loading && items.map((n) => (
          <ListItem key={n.id} onClick={() => setSelectedId(n.id)}>
            <ListTitle>[{n.target || '공지'}] {n.title}</ListTitle>
            <IoChevronForward size={16} color="#9CA3AF" />
          </ListItem>
        ))}
      </ListWrap>

      {total > PER_PAGE && (
        <Pagination>
          <PageBtn disabled={page <= 1} onClick={() => setPage(1)}>&laquo;</PageBtn>
          <PageBtn disabled={page <= 1} onClick={() => setPage(page - 1)}>&lt;</PageBtn>
          {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
            Math.max(0, page - 3),
            Math.max(0, page - 3) + 5
          ).map((p) => (
            <PageNum key={p} $active={p === page} onClick={() => setPage(p)}>{p}</PageNum>
          ))}
          <PageBtn disabled={page >= totalPages} onClick={() => setPage(page + 1)}>&gt;</PageBtn>
          <PageBtn disabled={page >= totalPages} onClick={() => setPage(totalPages)}>&raquo;</PageBtn>
        </Pagination>
      )}
    </Container>
  );
};

export default NotificationList;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ListWrap = styled.div``;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.base} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  cursor: pointer;
`;

const ListTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  color: ${({ theme }) => theme.colors.text};
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const PageBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.base};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const PageNum = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ $active, theme }) => $active ? theme.colors.primary : 'none'};
  color: ${({ $active }) => $active ? '#fff' : '#6B7280'};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ $active, theme }) => $active ? theme.fontWeight.semibold : theme.fontWeight.regular};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Meta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const DetailTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const DetailBody = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.xxxl};
  white-space: pre-wrap;
`;

const PrimaryBtn = styled.button`
  width: 100%;
  height: 48px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
`;
